import React, { useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Box,
  FlatTextInput,
  Form,
  getDuration,
  getValidationResolver,
  Preloader,
  useResponsive,
} from '@common-fe/common-fe';
import _ from 'lodash';
import * as yup from 'yup';

import { AUTH_ERROR_KEY, REQUIRED_TEXT } from '@/common/constants';
import AppButton from '@/components/controls/AppButton';
import {
  useHandleUnconfirmedUserEmailError,
  useHandleUnconfirmedUserError,
  useNavigateToPasswordResetLinkSent,
} from '@/modules/user/components/SignIn/hooks';
import { SignInErrorCode } from '@/modules/user/components/SignUp/signUp.constants';

import { useResendCodeTimerStore } from '../stores/useResendCodeTimer.store';

import { useForgotPassword } from './hooks';

const USERNAME_FIELD_KEY = 'forgotUsername';

interface ForgotPasswordProps {
  username: string;
}
const ForgotPassword: React.FC<ForgotPasswordProps> = ({ username }) => {
  const { isMobile } = useResponsive();
  const { t } = useTranslation();
  const { handleUnconfirmedUserError } = useHandleUnconfirmedUserError();
  const { handleUnconfirmedUserEmailError } = useHandleUnconfirmedUserEmailError();
  const { navigateToPasswordResetLinkSent } = useNavigateToPasswordResetLinkSent();
  const { setTimer } = useResendCodeTimerStore();
  const {
    control,
    formState: { errors },
    handleSubmit,
    setError,
  } = useForm({
    defaultValues: {
      [USERNAME_FIELD_KEY]: username,
    },

    resolver: getValidationResolver({
      [USERNAME_FIELD_KEY]: yup.string().required(REQUIRED_TEXT),
    }),
  });

  const { handleRequestCode, isLoading } = useForgotPassword();

  const handleSubmitForgotPassword = useCallback(
    async (value: object) => {
      const currentUsername = _.get(value, USERNAME_FIELD_KEY, '') as string;

      try {
        await handleRequestCode(currentUsername);
        setTimer({ expiredIn: `${Date.now() + getDuration({ minutes: 1 })}` });
        navigateToPasswordResetLinkSent(currentUsername);
      } catch (error) {
        const authErrorCode = _.get(error, 'response.data.auth_error_code', '') as string;
        const elevateErrorCode = _.get(error, 'response.data.elevate_error_code', '') as string;
        const verificationEmail = _.get(error, 'response.data.verification_email', '') as string;

        if (authErrorCode === SignInErrorCode.UNCONFIRMED_USER_ERROR_CODE) {
          setTimer({ expiredIn: `${Date.now() + getDuration({ minutes: 1 })}` });
          handleUnconfirmedUserError({ username: currentUsername, email: verificationEmail });
          return;
        }

        if (elevateErrorCode === SignInErrorCode.INVALID_USER_NAME_ERROR_CODE) {
          setError(USERNAME_FIELD_KEY, { message: AUTH_ERROR_KEY.USER_FORMAT_INVALID });
          return;
        }

        if (elevateErrorCode === SignInErrorCode.TOO_MANY_ATTEMPTS_ERROR_CODE) {
          setError(USERNAME_FIELD_KEY, { message: AUTH_ERROR_KEY.TOO_MANY_ATTEMPTS });
          return;
        }

        if (authErrorCode === SignInErrorCode.UNVERIFIED_EMAIL_ERROR_CODE) {
          setTimer({ expiredIn: `${Date.now() + getDuration({ minutes: 1 })}` });
          handleUnconfirmedUserEmailError({ username: currentUsername, email: verificationEmail });
          return;
        }

        if (elevateErrorCode === SignInErrorCode.FORGOT_USERNAME_ATTEMPTS_LIMIT_ERROR_CODE) {
          setError(USERNAME_FIELD_KEY, { message: AUTH_ERROR_KEY.TOO_MANY_FAILED_ATTEMPTS });
          return;
        }
      }
    },
    [
      handleRequestCode,
      navigateToPasswordResetLinkSent,
      handleUnconfirmedUserError,
      handleUnconfirmedUserEmailError,
      setError,
      setTimer,
    ]
  );

  return (
    <Box margin={{ horizontal: isMobile ? 'spacing12' : 'spacing16' }}>
      <Form onSubmit={handleSubmit(handleSubmitForgotPassword)}>
        <Box dir="column">
          <Controller
            name={USERNAME_FIELD_KEY}
            control={control}
            render={({ field: { value, onChange } }) => (
              <FlatTextInput
                value={value}
                onChange={onChange}
                className="text-input"
                testId="text-input-username"
                defaultAutoComplete="off"
                name={USERNAME_FIELD_KEY}
                isLarge
                placeholder={t('Enter your username')}
                errorText={_.get(errors, `${USERNAME_FIELD_KEY}.message`)}
                onBlur={() => {
                  onChange(value?.trim());
                }}
                isLowerCaseMode
              />
            )}
          />
          <Box margin={{ top: 'spacing32' }}>
            <AppButton
              testId="forgot-password-continue"
              type="submit"
              width="100%"
              size="L"
              disabled={isLoading}
            >
              {isLoading ? <Preloader color="white" /> : t('Continue')}
            </AppButton>
          </Box>
        </Box>
      </Form>
    </Box>
  );
};

export default ForgotPassword;
